/**
 * TerraMatrix Universal Data Platform - ChatContainer Component
 *
 * Sprint 5: Frontend - Data Page
 * Created: November 2025
 *
 * Container for displaying chat messages with auto-scroll.
 */

import React, { useRef, useEffect } from 'react';
import { ChatMessage } from '@/hooks/data/useDataQuery';
import { MessageBubble } from './MessageBubble';

interface ChatContainerProps {
  messages: ChatMessage[];
  isQuerying: boolean;
  onFollowUpClick?: (suggestion: string) => void;
}

export const ChatContainer: React.FC<ChatContainerProps> = ({
  messages,
  isQuerying,
  onFollowUpClick,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="space-y-4 p-4">
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message}>
          {/* Follow-up Suggestions */}
          {message.followUpSuggestions && message.followUpSuggestions.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {message.followUpSuggestions.map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => onFollowUpClick?.(suggestion)}
                  className="text-sm px-3 py-1.5 bg-muted rounded-full hover:bg-accent transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </MessageBubble>
      ))}

      {/* Loading Indicator */}
      {isQuerying && (
        <div className="flex items-center gap-2 text-muted-foreground px-4">
          <div className="flex gap-1">
            <span className="animate-bounce" style={{ animationDelay: '0ms' }}>
              ●
            </span>
            <span className="animate-bounce" style={{ animationDelay: '100ms' }}>
              ●
            </span>
            <span className="animate-bounce" style={{ animationDelay: '200ms' }}>
              ●
            </span>
          </div>
          <span>Querying...</span>
        </div>
      )}

      {/* Scroll anchor */}
      <div ref={bottomRef} />
    </div>
  );
};

export default ChatContainer;
